/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-explicit-any -- pre-existing legacy code, not touched in this pass; typing this properly is tracked separately */
// @ts-nocheck
import { CSSProperties } from "react";
import { ResponseContainer, SpacesResponse, XYZ } from "store";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
// @ts-ignore
import { createBox, createSceneObjects } from "utils/renderUtils.js";

import TWEEN, { Tween } from "@tweenjs/tween.js";

type Size = {
  width: number;
  height: number;
};
type ContainerFloor = {
  size: XYZ;
  complex: XYZ;
  location: XYZ;
  uid: string;
  color: CSSProperties["color"];
  opacity?: number;
};

function getLogStack() {
  try {
    throw new Error();
  } catch (error) {
    // @ts-ignore
    const stackTrace = error.stack;
    return stackTrace;
  }
}

class WebRender {
  private data: ResponseContainer;
  private size: Size;
  public cameraPosition: XYZ;
  private readonly pallet: SpacesResponse[];
  private readonly maxSize: number;
  private readonly pixelWorldSize: number;
  public readonly initialPosition: XYZ;
  public readonly initialPositionThumb: XYZ;
  private readonly sizeCoefficient: number;
  private readonly containerFloor: ContainerFloor;

  private readonly offsetCoefficient = 50;
  private camera: any;
  private scene: THREE.Scene | undefined;
  private isRenderStepByStep?: boolean = false;
  private objectsAdded = false;
  private displayLimit: number | null = null;
  private collectStoryboardFrames = false;
  isPaused = false;
  isStopped = true;
  storyBoardData: string[] = [];
  displayedStep = 0;

  constructor(data: ResponseContainer, size: Size = { width: 400, height: 300 }) {
    this.data = data;
    this.size = size;
    this.pallet = data.spaces;

    this.sizeCoefficient = 1000 / Math.min(data.size.x, data.size.y, data.size.z);

    const containerSize: XYZ = {
      x: (data.size.x + this.offsetCoefficient * 2) * this.sizeCoefficient,
      y: (data.size.y + this.offsetCoefficient * 2) * this.sizeCoefficient,
      z: data.size.z * this.sizeCoefficient,
    };
    this.maxSize = Math.max(containerSize.x, containerSize.y, containerSize.z);
    // World-space size of one screen pixel under the orthographic camera
    // (frustum height = 2*maxSize/1.62, mapped onto this.size.height px).
    // Used to shrink cargo boxes by a screen-constant amount so a visible
    // seam appears between adjacent boxes regardless of zoom/container size.
    this.pixelWorldSize = (2 * this.maxSize) / (1.62 * this.size.height);

    this.cameraPosition = {
      x: 20307.23127527466 * this.sizeCoefficient,
      y: 11575.763978921625 * this.sizeCoefficient,
      z: 13316.958624962197 * this.sizeCoefficient,
    };

    this.initialPosition = {
      x: containerSize.x / 2,
      y: 500,
      z: containerSize.z / 2,
    };
    this.initialPositionThumb = {
      x: containerSize.x / 2,
      y: 0,
      z: containerSize.z / 2,
    };
    this.containerFloor = {
      size: {
        x: containerSize.x,
        y: containerSize.y,
        z: containerSize.z / 30,
      },
      complex: { x: 1, y: 1, z: 1 },
      location: {
        x: -this.offsetCoefficient * this.sizeCoefficient,
        y: -this.offsetCoefficient * this.sizeCoefficient,
        z: -containerSize.z / 30,
      },
      uid: "container-floor-object",
      color: "#9AA5AE",
      opacity: 0.75,
    };
  }

  public setCollectStoryboard(enabled: boolean) {
    this.collectStoryboardFrames = enabled;
  }

  public init(type?: "thumb" | "render") {
    let renderer = new THREE.WebGLRenderer();
    const aspect = this.size.width / this.size.height;
    const initialPosition = type === "thumb" ? this.initialPositionThumb : this.initialPosition;

    this.camera = new THREE.OrthographicCamera(
      (-aspect * this.maxSize) / 1.62,
      (aspect * this.maxSize) / 1.62,
      this.maxSize / 1.62,
      -this.maxSize / 1.62,
      1,
      1000000 * this.sizeCoefficient
    );

    this.camera.target = initialPosition;
    this.camera.position.set(this.cameraPosition.x, this.cameraPosition.y, this.cameraPosition.z);
    let controls = new OrbitControls(this.camera, renderer.domElement);
    let scene = new THREE.Scene();

    let ambient = new THREE.AmbientLight(0xffffff, 0.6);
    let light = new THREE.HemisphereLight(0xffffff, 0x080820, 0.4);
    light.position.set(this.cameraPosition.x, this.cameraPosition.y, this.cameraPosition.z);

    const backLight = new THREE.PointLight(0xffffff, 0.2);
    backLight.position.set(-this.cameraPosition.x, -this.cameraPosition.y, -this.cameraPosition.z);

    createBox(scene, this.containerFloor);
    this.addGroundGrid(scene);

    this.addContainerBorder(scene);

    scene.add(light, ambient);
    scene.add(backLight, ambient);
    this.camera.updateProjectionMatrix();
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0xffffff);
    renderer.setSize(this.size.width, this.size.height);

    controls.enableDamping = true;
    controls.screenSpacePanning = false;
    controls.maxPolarAngle = Math.PI / 2;

    controls.minDistance = this.maxSize * this.sizeCoefficient;
    controls.maxDistance = 1000000 * this.sizeCoefficient;

    controls.target.set(initialPosition.x, initialPosition.y, initialPosition.z);

    this.scene = scene;

    if (this.isRenderStepByStep) {
      this.addObjectsStepByStep(this.pallet, scene);
    } else {
      const limit = this.displayLimit ?? this.pallet.length;
      const items = this.pallet.slice(0, Math.max(0, limit));
      const collectStoryboard =
        this.collectStoryboardFrames &&
        !this.isRenderStepByStep &&
        limit >= this.pallet.length &&
        this.storyBoardData.length === 0;
      items.forEach((item: any, index: number) => {
        createSceneObjects(scene, item, this.maxSize, this.sizeCoefficient, index, this.pixelWorldSize);
        if (collectStoryboard) {
          this.storyBoardData.push(this.getThumbnail({ renderer, camera: this.camera, scene, controls }));
        }
      });
      this.displayedStep = items.length;
      this.objectsAdded = items.length >= this.pallet.length;
    }

    return { renderer, camera: this.camera, scene, controls };
  }

  public render(domElement: HTMLDivElement, isRenderStepByStep?: boolean) {
    this.isRenderStepByStep = isRenderStepByStep;
    if (!isRenderStepByStep && this.displayLimit === null) {
      this.displayLimit = this.pallet.length;
    }
    const { renderer, camera, scene, controls } = this.init();

    domElement.innerHTML = "";
    domElement.appendChild(renderer.domElement);

    const animate = () => {
      if (this.size.width && this.size.height) {
        renderer.setSize(this.size.width, this.size.height);
        // @ts-ignore
        TWEEN.update();
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      }
    };
    animate();
  }

  public renderAtStep(domElement: HTMLDivElement, step: number) {
    const clamped = Math.max(0, Math.min(step, this.pallet.length));
    this.isRenderStepByStep = false;
    this.isStopped = true;
    this.isPaused = false;
    this.objectsAdded = clamped >= this.pallet.length;
    this.displayLimit = clamped;
    this.storyBoardData = [];
    this.render(domElement, false);
    return clamped;
  }

  public getTotalSteps() {
    return this.pallet.length;
  }

  public addObjectsStepByStepPause() {
    this.isPaused = true;
  }

  public addObjectsStepByStepResume() {
    this.isPaused = false;
  }

  public addObjectsStepByStepStopToggle() {
    if (this.objectsAdded) {
      this.addObjectsStepByStepStart();
      return;
    }
    this.isStopped = !this.isStopped;
  }

  public addObjectsStepByStepStart() {
    this.isStopped = false;
    this.objectsAdded = false;
  }

  public addObjectsStepByStep(items: SpacesResponse[], scene: THREE.Scene) {
    if (this.objectsAdded) return;

    let i = 0;
    const addNextObject = () => {
      if (this.isStopped || i >= items.length) return;

      if (!this.isPaused) {
        const item = items[i];
        createSceneObjects(scene, item, this.maxSize, this.sizeCoefficient, i, this.pixelWorldSize);
        i++;
      }

      if (i < items.length) {
        setTimeout(() => requestAnimationFrame(addNextObject), 1000);
      } else {
        this.objectsAdded = true;
      }
    };

    requestAnimationFrame(addNextObject);
  }

  public removeObjects() {
    const { renderer, scene } = this.init();

    while (scene.children.length > 0) {
      const child = scene.children[0];
      scene.remove(child);

      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((material) => {
              if (material) {
                material.dispose();
              }
            });
          } else {
            if (child.material) {
              child.material.dispose();
            }
          }
        }
      } else if (child instanceof THREE.Light) {
        if (child.shadow && child.shadow.map) {
          child.shadow.map.dispose();
        }
      }
    }

    if (renderer && renderer.dispose) {
      renderer.dispose();
      renderer.clear();
    }
  }

  public getThumbnail(renderData?: {
    renderer: THREE.Renderer;
    camera: THREE.Camera;
    scene: THREE.Scene;
    controls: OrbitControls;
  }) {
    let thumbnail: string = "";

    const previousCollect = this.collectStoryboardFrames;
    if (!renderData) {
      this.collectStoryboardFrames = false;
      this.displayLimit = this.pallet.length;
    }

    const { renderer, camera, scene, controls } = renderData ?? this.init("thumb");
    this.collectStoryboardFrames = previousCollect;

    if (this.size.width && this.size.height) {
      renderer.setSize(this.size.width, this.size.height);
      controls.update();
      renderer.render(scene, camera);
      thumbnail = renderer.domElement.toDataURL("image/png");
    }

    if (!renderData && "dispose" in renderer && typeof renderer.dispose === "function") {
      renderer.dispose();
    }

    return thumbnail;
  }

  public resetCamera() {
    const position = new THREE.Vector3(this.cameraPosition.x, this.cameraPosition.y, this.cameraPosition.z);

    new Tween(this.camera.position).to(position, 1000).easing(TWEEN.Easing.Quadratic.InOut).start();
  }

  // Thin wireframe cuboid outlining the container (offsetCoefficient margin
  // on every side, matching the cargo layout's own breathing room) instead
  // of extruded corner/ceiling boxes — a crisp, camera-independent line.
  private addContainerBorder(scene: THREE.Scene) {
    const offset = this.offsetCoefficient * this.sizeCoefficient;
    const width = this.data.size.x * this.sizeCoefficient + offset * 2;
    const depth = this.data.size.y * this.sizeCoefficient + offset * 2;
    const height = this.data.size.z * this.sizeCoefficient;

    const boxGeometry = new THREE.BoxGeometry(width, height, depth);
    const edges = new THREE.EdgesGeometry(boxGeometry);

    // Plain LineBasicMaterial ignores its `linewidth` on most GPUs/browsers
    // (capped to 1px) — LineMaterial/LineSegments2 render "fat lines" via a
    // screen-space shader instead, so a requested pixel width actually shows.
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(edges.attributes.position.array as Float32Array);

    const material = new LineMaterial({
      linewidth: 1.4, // CSS pixels (LineMaterial.worldUnits defaults to false)
      // opacity: 0.6,
      // transparent: true,
      resolution: new THREE.Vector2(this.size.width, this.size.height),
    });
    material.color = new THREE.Color("#AEB4BB");

    const wireframe = new LineSegments2(geometry, material);
    wireframe.position.set(
      (this.data.size.x * this.sizeCoefficient) / 2,
      height / 2,
      (this.data.size.y * this.sizeCoefficient) / 2
    );
    wireframe.name = "container-wireframe";

    scene.add(wireframe);
  }

  // Light grid on the container floor, matching its exact footprint —
  // gives the "loading deck" look. Drawn as thin flat boxes rather than
  // THREE.Line: WebGL caps line width at 1px on most platforms, which made
  // an actual Line-based grid all but invisible.
  // Ground the container stands on — a gray semi-transparent slab with a
  // grid, sized *larger* than the container footprint (unlike the
  // container's own floor slab above, which stays inside its walls).
  // Sits just beneath that floor slab so the container visibly rests on it.
  private addGroundGrid(scene: THREE.Scene) {
    const floor = this.containerFloor;
    const margin = Math.max(floor.size.x, floor.size.y) * 0.35;
    const groundThickness = Math.max(4, floor.size.z * 0.5);

    const size = { x: floor.size.x + margin * 2, y: floor.size.y + margin * 2, z: groundThickness };
    const location = {
      x: floor.location.x - margin,
      y: floor.location.y - margin,
      // Sits right under the container's own floor slab, not flush with it,
      // so the two don't z-fight where their footprints overlap.
      z: floor.location.z - groundThickness,
    };

    // createBox(scene, {
    //   complex: { x: 1, y: 1, z: 1 },
    //   size,
    //   location,
    //   uid: "scene-ground-object",
    //   color: "#B7BEC6",
    //   opacity: 0.004,
    // });

    const groundTop = location.z + size.z;
    const step = Math.max(size.x, size.y) / 16 || 1;
    const lineThickness = Math.max(1, Math.min(size.x, size.y) * 0.0006);
    const lineHeight = 1;
    const color = "#7C8791";
    const opacity = 0.3;

    let index = 0;
    for (let x = 0; x <= size.x + 1e-6; x += step) {
      const centerX = location.x + Math.min(x, size.x);
      createBox(scene, {
        complex: { x: 1, y: 1, z: 1 },
        size: { x: lineThickness, y: size.y, z: lineHeight },
        location: { x: centerX - lineThickness / 2, y: location.y, z: groundTop },
        uid: `scene-ground-grid-x-${index++}`,
        color,
        opacity,
      });
    }
    for (let y = 0; y <= size.y + 1e-6; y += step) {
      const centerY = location.y + Math.min(y, size.y);
      createBox(scene, {
        complex: { x: 1, y: 1, z: 1 },
        size: { x: size.x, y: lineThickness, z: lineHeight },
        location: { x: location.x, y: centerY - lineThickness / 2, z: groundTop },
        uid: `scene-ground-grid-y-${index++}`,
        color,
        opacity,
      });
    }
  }

  save(blob: Blob, filename: string) {
    const link = document.createElement("a");
    link.style.display = "none";
    document.body.appendChild(link);
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  saveArrayBuffer(buffer: ArrayBuffer, filename: string) {
    this.save(new Blob([buffer], { type: "application/octet-stream" }), filename);
  }

  saveString(text: string, filename: string) {
    this.save(new Blob([text], { type: "text/plain" }), filename);
  }

  exportToGLTF(filename: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.scene) {
        reject(new Error("Scene is not ready"));
        return;
      }
      const exporter = new GLTFExporter();

      const options = {
        binary: false,
        trs: false,
        onlyVisible: true,
        truncateDrawRange: true,
        embedImages: true,
      };

      exporter.parse(
        this.scene,
        (result) => {
          if (result instanceof ArrayBuffer) {
            this.saveArrayBuffer(result, `${filename}.glb`);
          } else {
            const output = JSON.stringify(result, null, 2);
            this.saveString(output, `${filename}.gltf`);
          }
          resolve();
        },
        (error) => {
          // eslint-disable-next-line no-console -- surfaces GLTF export failures during dev
          console.log("An error happened during parsing", error);
          reject(error);
        },
        options
      );
    });
  }
}

export default WebRender;
